import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createCampaign } from '../api/client';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import '../components/ui/ui-input';
import { alertStyles, boxSizingStyles } from '../styles/lit-shared';

@customElement('campaign-create-form')
export class CampaignCreateForm extends LitElement {
  static styles = [
    boxSizingStyles,
    alertStyles,
    css`
      :host {
        display: block;
      }

      form {
        display: flex;
        flex-direction: column;
        gap: var(--space-lg);
      }

      .hint-block {
        padding: var(--space-sm) var(--space-md);
        border-radius: var(--radius-sm);
        background: rgb(139 92 246 / 8%);
        border: 1px solid rgb(139 92 246 / 20%);
        font-size: 0.8125rem;
        color: var(--color-text-muted);
        line-height: 1.55;
      }

      .hint-block strong {
        color: var(--color-primary-light);
        font-weight: 600;
      }

      .phase-list {
        padding: var(--space-sm) var(--space-md);
        border-radius: var(--radius-sm);
        background: rgb(0 0 0 / 20%);
        border: 1px solid var(--color-border);
        font-size: 0.8125rem;
        color: var(--color-text-muted);
        line-height: 1.7;
        list-style: none;
        margin: 0;
      }

      .phase-list li::before {
        content: '→ ';
        color: var(--color-primary-light);
        font-weight: 600;
      }
    `,
  ];

  @state() private topic = '';
  @state() private targetAudience = '';
  @state() private finalObjective = '';
  @state() private loading = false;
  @state() private error = '';

  private handleTopicChange(e: CustomEvent<{ value: string }>) {
    this.topic = e.detail.value;
  }

  private handleTargetChange(e: CustomEvent<{ value: string }>) {
    this.targetAudience = e.detail.value;
  }

  private handleObjectiveChange(e: CustomEvent<{ value: string }>) {
    this.finalObjective = e.detail.value;
  }

  private validate(): string | null {
    if (!this.topic.trim()) return 'El tema de la campaña es obligatorio.';
    if (!this.targetAudience.trim()) return 'El cliente ideal es obligatorio.';
    if (!this.finalObjective.trim()) return 'El objetivo de conversión es obligatorio.';
    return null;
  }

  async submit(): Promise<void> {
    const validationError = this.validate();
    if (validationError) {
      this.error = validationError;
      return;
    }

    this.error = '';
    this.loading = true;

    try {
      const result = await createCampaign({
        topic: this.topic.trim(),
        targetAudience: this.targetAudience.trim(),
        finalObjective: this.finalObjective.trim(),
      });

      this.dispatchEvent(
        new CustomEvent('campaign-created', {
          detail: { campaignId: result.campaignId },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      this.error =
        err instanceof ApiClientError
          ? err.message
          : 'Error inesperado. Inténtalo de nuevo.';
    } finally {
      this.loading = false;
    }
  }

  render() {
    return html`
      <form @submit=${(e: Event) => { e.preventDefault(); void this.submit(); }}>
        <div class="hint-block">
          El <strong>Director Estratégico Blunticle</strong> diseñará un plan maestro de
          <strong>10 posts</strong> con evolución narrativa y tono canalla para convertir
          a tu audiencia en el post final.
        </div>

        <ui-input
          label="Tema de la campaña"
          name="topic"
          placeholder="Ej: Por qué dejar el café y pasarse al té matcha"
          .value=${this.topic}
          .maxlength=${300}
          required
          @value-changed=${this.handleTopicChange}
        ></ui-input>

        <ui-input
          label="Cliente ideal (Target)"
          name="targetAudience"
          placeholder="Ej: Programadores estresados de 25-35 años que viven a base de café"
          .value=${this.targetAudience}
          .maxlength=${500}
          required
          @value-changed=${this.handleTargetChange}
        ></ui-input>

        <ui-input
          label="Objetivo de conversión (Post 10)"
          name="finalObjective"
          placeholder="Ej: Que se suscriban a la newsletter premium a 9€/mes"
          .value=${this.finalObjective}
          .maxlength=${300}
          required
          @value-changed=${this.handleObjectiveChange}
        ></ui-input>

        <ul class="phase-list">
          <li>Posts 1–3: Romper el statu quo (mitos, agitación)</li>
          <li>Posts 4–6: Cambio de perspectiva (nuevo vehículo)</li>
          <li>Posts 7–9: Autoridad y objeciones</li>
          <li>Post 10: El Clímax — venta directa</li>
        </ul>

        ${this.error
          ? html`<div class="alert alert--error">${this.error}</div>`
          : ''}

        <ui-button
          fullwidth
          ?loading=${this.loading}
          ?disabled=${this.loading}
          @ui-click=${() => void this.submit()}
        >
          ${this.loading ? 'Diseñando campaña…' : 'Generar Campaña'}
        </ui-button>
      </form>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'campaign-create-form': CampaignCreateForm;
  }
}
